import * as React from 'react';
import { Layout, Menu, Spin, Alert, Typography, Button, Space, Drawer } from 'antd';
import { LogoutOutlined, MenuOutlined } from '@ant-design/icons';
import { getCollection } from '../firebase/firestore';
import { signOutUser, getCurrentUser } from '../firebase/auth';
import { ShopActions } from './ShopActions';

const { Sider, Content, Header } = Layout;
const { Title, Text } = Typography;

interface Shop {
    id: string;
    url: string;
    scope?: string;
    source?: string;
    syncProductsFromComax?: boolean;
    generateComaxInvoices?: boolean;
    name?: string;
    email?: string;
    comaxOnlineCustomerId?: string;
    comaxOnlineStoreId?: string;
    comaxToShopifyLocations?: Record<string, number>;
    comaxStoreIds?: string;
    shippingItemBarcode?: string;
    discountItemBarcode?: string;
    shopifyToken?: string;
    comaxUsername?: string;
    comaxPassword?: string;
    isActive?: boolean;
    adjustInventoryBy?: number;
    returnShippingFee?: number;
    [key: string]: any;
}

export function Admin(): React.ReactElement {
    const [shops, setShops] = React.useState<Shop[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<string | null>(null);
    const [selectedShop, setSelectedShop] = React.useState<Shop | null>(null);
    const [signingOut, setSigningOut] = React.useState<boolean>(false);
    const [user] = React.useState(getCurrentUser());
    const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth < 768);
    const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);

    React.useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    React.useEffect(() => {
        async function fetchShops() {
            try {
                setLoading(true);
                setError(null);
                const shopsData = await getCollection<Shop>('shops');
                setShops(shopsData);
                // Select first shop by default if available
                if (shopsData.length > 0) {
                    setSelectedShop(shopsData[0]);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch shops');
                console.error('Error fetching shops:', err);
            } finally {
                setLoading(false);
            }
        }

        fetchShops();
    }, []);

    const handleMenuClick = (shopId: string) => {
        const shop = shops.find(s => s.id === shopId);
        if (shop) {
            setSelectedShop(shop);
            if (isMobile) {
                setDrawerOpen(false);
            }
        }
    };

    const handleSignOut = async () => {
        try {
            setSigningOut(true);
            await signOutUser();
        } catch (err) {
            console.error('Error signing out:', err);
        } finally {
            setSigningOut(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" tip="Loading shops..." />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert message="Error" description={error} type="error" showIcon />
            </div>
        );
    }

    const menuItems = shops.map(shop => ({
        key: shop.id,
        label: shop.name || shop.url || shop.id,
    }));


    const menuContent = (
        <>
            <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
                <Title level={4} style={{ margin: 0 }}>Shops</Title>
            </div>
            {shops.length === 0 ? (
                <div style={{ padding: '16px', color: '#999' }}>No shops found</div>
            ) : (
                <Menu
                    mode="inline"
                    selectedKeys={selectedShop ? [selectedShop.id] : []}
                    items={menuItems}
                    onClick={({ key }) => handleMenuClick(key)}
                    style={{ borderRight: 0 }}
                />
            )}
        </>
    );

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Header style={{ 
                background: '#fff', 
                padding: isMobile ? '0 16px' : '0 24px', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: '1px solid #f0f0f0'
            }}>
                <Space>
                    {isMobile && (
                        <Button
                            type="text"
                            icon={<MenuOutlined />}
                            onClick={() => setDrawerOpen(true)}
                            style={{ marginRight: '8px' }}
                        />
                    )}
                    <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>Admin Panel</Title>
                </Space>
                <Space>
                    {!isMobile && (
                        <Text type="secondary" style={{ fontSize: '14px' }}>{user?.email}</Text>
                    )}
                    <Button 
                        icon={<LogoutOutlined />} 
                        onClick={handleSignOut}
                        loading={signingOut}
                        size={isMobile ? 'small' : 'middle'}
                    >
                        {isMobile ? '' : 'Sign Out'}
                    </Button>
                </Space>
            </Header>
            <Layout>
                {isMobile ? (
                    <Drawer
                        title="Shops"
                        placement="left"
                        onClose={() => setDrawerOpen(false)}
                        open={drawerOpen}
                        width={280}
                        bodyStyle={{ padding: 0 }}
                    >
                        {menuContent}
                    </Drawer>
                ) : (
                    <Sider width={300} style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}>
                        {menuContent}
                    </Sider>
                )}
                <Content style={{ 
                    padding: isMobile ? '16px' : '24px', 
                    background: '#fff',
                    minHeight: 'calc(100vh - 64px)'
                }}>
                    {selectedShop ? (
                        <div>
                            <Title level={isMobile ? 3 : 2} style={{ marginBottom: isMobile ? '24px' : '32px' }}>
                                {selectedShop.name || selectedShop.url}
                            </Title>
                            <ShopActions shop={selectedShop} />
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: isMobile ? '32px 16px' : '48px', color: '#999' }}>
                            {isMobile ? (
                                <Text>Select a shop from the menu</Text>
                            ) : (
                                <Text>Select a shop from the sidebar to view actions</Text>
                            )}
                        </div>
                    )}
                </Content>
            </Layout>
        </Layout>
    );
}

